import { useRef, useEffect, useState } from 'react';
import { useAppSelector } from 'app/hooks';
import { selectTraceDataBySnapshotId } from 'features/spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import {
  drawSpectrogram,
  trace_data_shift,
  newTraceData,
  handleResize,
  clearCanvas,
  drawChartLabels,
  drawFrame,
} from './utils/spectogram.utils';
import { useTraceHistoryByLimit } from 'features/traceHistory/api/getHistoryByLimit';
import { parseTraceFromBuffer } from 'utils/traceUtils';

interface Props {
  analyzerId: number;
  snapshotId: number;
  hideLabels?: boolean | null | undefined;
}

const Spectrogram = ({ analyzerId, snapshotId, hideLabels }: Props) => {
  const [currentHeight, setCurrentHeight] = useState<number | undefined>(
    undefined
  );
  let data = useAppSelector((state) =>
    selectTraceDataBySnapshotId(state, snapshotId)
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const virtualContext = useRef<{
    elem: HTMLCanvasElement | null;
    ctx: CanvasRenderingContext2D | null;
  }>({
    elem: null,
    ctx: null,
  });

  const { data: analyzer } = useAnalyzer({ analyzerId });
  const { data: snapshot } = useSnapshot({ snapshotId });
  const { data: history } = useTraceHistoryByLimit({
    snapshotId,
    limit: currentHeight,
  });

  useEffect(() => {
    const canvas: HTMLCanvasElement | null | undefined = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    if (!virtualContext.current.elem) {
      //@ts-ignore
      virtualContext.current.elem = document.createElement('canvas');
      //@ts-ignore
      virtualContext.current.ctx = virtualContext.current.elem.getContext('2d');
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!virtualContext.current.elem) return;

    const canvas: HTMLCanvasElement | null = canvasRef.current;
    const ctx: CanvasRenderingContext2D | null | undefined =
      canvas?.getContext('2d');
    if (!ctx || !snapshot || !history) return;

    for (let i = history.length - 1; i > 0; i--) {
      let historyItem = history[i];
      const parsedTrace = parseTraceFromBuffer(historyItem.trace);
      clearCanvas(ctx);
      drawFrame(ctx);
      trace_data_shift(virtualContext.current.ctx);
      newTraceData(
        parsedTrace,
        snapshot?.reference,
        snapshot?.scale,
        virtualContext.current.ctx
      );
      drawSpectrogram(virtualContext.current.ctx, ctx);
      drawChartLabels(
        ctx,
        analyzer?.name,
        snapshot?.name,
        snapshot?.reference,
        snapshot?.scale
      );
    }
    //eslint-disable-next-line
  }, [history]);

  useEffect(() => {
    if (!virtualContext.current.elem) return;

    const canvas: HTMLCanvasElement | null = canvasRef.current;
    const ctx: CanvasRenderingContext2D | null | undefined =
      canvas?.getContext('2d');
    if (!ctx || !snapshot) return;
    clearCanvas(ctx);
    drawFrame(ctx);
    trace_data_shift(virtualContext.current.ctx);
    newTraceData(
      data,
      snapshot?.reference,
      snapshot?.scale,
      virtualContext.current.ctx
    );
    drawSpectrogram(virtualContext.current.ctx, ctx);
    drawChartLabels(
      ctx,
      analyzer?.name,
      snapshot?.name,
      snapshot?.reference,
      snapshot?.scale
    );
  }, [
    data,
    snapshot,
    snapshot?.reference,
    snapshot?.scale,
    analyzer?.name,
    snapshot?.name,
    hideLabels,
  ]);

  useEffect(() => {
    const canvas: HTMLCanvasElement | null = canvasRef.current;
    const ctx: CanvasRenderingContext2D | null | undefined =
      canvas?.getContext('2d');
    if (!ctx) return;
    handleResize(
      ctx,
      analyzer?.name,
      snapshot?.name,
      snapshot?.reference,
      snapshot?.scale
    );
    setCurrentHeight(ctx.canvas.clientHeight);
    window.addEventListener('resize', () =>
      handleResize(
        ctx,
        analyzer?.name,
        snapshot?.name,
        snapshot?.reference,
        snapshot?.scale
      )
    );
    return () => {
      window.removeEventListener('resize', () =>
        handleResize(
          ctx,
          analyzer?.name,
          snapshot?.name,
          snapshot?.reference,
          snapshot?.scale
        )
      );
    };
    //eslint-disable-next-line
  }, []);

  return (
    <div>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default Spectrogram;
